import type { TemplateFormLanguage } from '../types/templateForms';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from './constants';

export interface FileUploadTranslations {
    upload: string;
    uploadFile: string;
    loading: string;
    unknownFile: string;
    failedToLoadDetails: string;
    fileNameTooLong: string;
    failedToUpload: string;
    noFilesSelected: string;
    onlyOneFile: string;
    onlyOneFileAtATime: string;
    onlyNFilesAtATime: (count: number) => string;
    fileSizeMustBeLessThan: (mb: number) => string;
    invalidFileTypeIdDocuments: string;
    invalidFileTypeRetainer: string;
    failedToDelete: string;
    ariaFileUpload: string;
    ariaChooseFile: string;
    dragAndDropHint: string;
}

const ENGLISH_TRANSLATIONS: FileUploadTranslations = {
    upload: 'Upload',
    uploadFile: 'Upload file',
    loading: 'Loading...',
    unknownFile: 'Unknown file',
    failedToLoadDetails: 'Failed to load file details',
    fileNameTooLong: 'File name is too long. File name must be 400 characters or less.',
    failedToUpload: 'Failed to upload the file!',
    noFilesSelected: 'No files selected!',
    onlyOneFile: 'You can only upload 1 file at a time',
    onlyOneFileAtATime: 'You can only upload one file at a time.',
    onlyNFilesAtATime: (count) => `You can only upload ${count} file/s at a time.`,
    fileSizeMustBeLessThan: (mb) => `File size must be less than ${mb}MB.`,
    invalidFileTypeIdDocuments:
        'Invalid file type. Accepted formats: DOC, PDF, PNG, JPG. Please upload a file in one of these formats.',
    invalidFileTypeRetainer:
        'Invalid file type. Accepted formats: DOCX, PDF (optional AcroForm fields for autofill).',
    failedToDelete: 'Failed to delete the file!',
    ariaFileUpload: 'File upload',
    ariaChooseFile: 'Choose a file',
    dragAndDropHint: 'or drag and drop here',
};

const FRENCH_TRANSLATIONS: FileUploadTranslations = {
    upload: 'Téléverser',
    uploadFile: 'Téléverser un fichier',
    loading: 'Chargement...',
    unknownFile: 'Fichier inconnu',
    failedToLoadDetails: 'Échec du chargement des détails du fichier',
    fileNameTooLong: 'Le nom du fichier est trop long. Il doit comporter 400 caractères ou moins.',
    failedToUpload: 'Échec du téléversement du fichier!',
    noFilesSelected: 'Aucun fichier sélectionné!',
    onlyOneFile: 'Vous ne pouvez téléverser qu’un seul fichier à la fois',
    onlyOneFileAtATime: 'Vous ne pouvez téléverser qu’un seul fichier à la fois.',
    onlyNFilesAtATime: (count) => `Vous ne pouvez téléverser que ${count} fichier(s) à la fois.`,
    fileSizeMustBeLessThan: (mb) => `La taille du fichier doit être inférieure à ${mb} Mo.`,
    invalidFileTypeIdDocuments:
        'Type de fichier invalide. Formats acceptés : DOC, PDF, PNG, JPG. Veuillez téléverser un fichier dans l’un de ces formats.',
    invalidFileTypeRetainer:
        'Type de fichier invalide. Formats acceptés : DOCX, PDF (champs AcroForm optionnels pour le remplissage automatique).',
    failedToDelete: 'Échec de la suppression du fichier!',
    ariaFileUpload: 'Téléversement de fichier',
    ariaChooseFile: 'Choisir un fichier',
    dragAndDropHint: 'ou glissez-déposez ici',
};

export const FILE_UPLOAD_TRANSLATIONS: Record<TemplateFormLanguage, FileUploadTranslations> = {
    en: ENGLISH_TRANSLATIONS,
    fr: FRENCH_TRANSLATIONS,
};

export function getFileUploadTranslations(
    language: TemplateFormLanguage = DEFAULT_TEMPLATE_FORM_LANGUAGE,
): FileUploadTranslations {
    return FILE_UPLOAD_TRANSLATIONS[language] ?? FILE_UPLOAD_TRANSLATIONS.en;
}
